import React, { useMemo } from "react";

import { IconDefinition } from "@fortawesome/fontawesome-common-types";
import { faFile as faFileLight } from "@fortawesome/pro-light-svg-icons/faFile";
import { faFileAlt as faFileAltLight } from "@fortawesome/pro-light-svg-icons/faFileAlt";
import { faFileAudio as faFileAudioLight } from "@fortawesome/pro-light-svg-icons/faFileAudio";
import { faFileCode as faFileCodeLight } from "@fortawesome/pro-light-svg-icons/faFileCode";
import { faFileCsv as faFileCsvLight } from "@fortawesome/pro-light-svg-icons/faFileCsv";
import { faFileExcel as faFileExcelLight } from "@fortawesome/pro-light-svg-icons/faFileExcel";
import { faFileImage as faFileImageLight } from "@fortawesome/pro-light-svg-icons/faFileImage";
import { faFilePdf as faFilePdfLight } from "@fortawesome/pro-light-svg-icons/faFilePdf";
import { faFilePowerpoint as faFilePowerpointLight } from "@fortawesome/pro-light-svg-icons/faFilePowerpoint";
import { faFileVideo as faFileVideoLight } from "@fortawesome/pro-light-svg-icons/faFileVideo";
import { faFileWord as faFileWordLight } from "@fortawesome/pro-light-svg-icons/faFileWord";
import { faFileXml as faFileXmlLight } from "@fortawesome/pro-light-svg-icons/faFileXml";
import { faFileZip as faFileZipLight } from "@fortawesome/pro-light-svg-icons/faFileZip";
import { faFile as faFileSolid } from "@fortawesome/pro-solid-svg-icons/faFile";
import { faFileAlt as faFileAltSolid } from "@fortawesome/pro-solid-svg-icons/faFileAlt";
import { faFileAudio as faFileAudioSolid } from "@fortawesome/pro-solid-svg-icons/faFileAudio";
import { faFileCode as faFileCodeSolid } from "@fortawesome/pro-solid-svg-icons/faFileCode";
import { faFileCsv as faFileCsvSolid } from "@fortawesome/pro-solid-svg-icons/faFileCsv";
import { faFileExcel as faFileExcelSolid } from "@fortawesome/pro-solid-svg-icons/faFileExcel";
import { faFileImage as faFileImageSolid } from "@fortawesome/pro-solid-svg-icons/faFileImage";
import { faFilePdf as faFilePdfSolid } from "@fortawesome/pro-solid-svg-icons/faFilePdf";
import { faFilePowerpoint as faFilePowerpointSolid } from "@fortawesome/pro-solid-svg-icons/faFilePowerpoint";
import { faFileVideo as faFileVideoSolid } from "@fortawesome/pro-solid-svg-icons/faFileVideo";
import { faFileWord as faFileWordSolid } from "@fortawesome/pro-solid-svg-icons/faFileWord";
import { faFileXml as faFileXmlSolid } from "@fortawesome/pro-solid-svg-icons/faFileXml";
import { faFileZip as faFileZipSolid } from "@fortawesome/pro-solid-svg-icons/faFileZip";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { AvatarProps } from "antd/es/avatar";
import Avatar from "antd/es/avatar/avatar";
import cn from "classnames";

import styles from "./FileIcon.module.scss";

type IconVariants = {
  regular: IconDefinition;
  solid: IconDefinition;
};

export enum FileType {
  AUDIO = "audio",
  DOCUMENT = "document",
  IMAGE = "image",
  OTHER = "other",
  PDF = "pdf",
  SPREADSHEET = "spreadsheet",
  VIDEO = "video",
  CSV = "csv",
  ZIP = "zip",
  TEXT = "text",
  PRESENTATION = "presentation",
  MARKDOWN = "markdown",
  XML = "xml",
  CODE = "code",
}

export const fileTypeToIcons: Record<FileType, IconVariants> = {
  [FileType.AUDIO]: {
    regular: faFileAudioLight,
    solid: faFileAudioSolid,
  },
  [FileType.CODE]: {
    regular: faFileCodeLight,
    solid: faFileCodeSolid,
  },
  [FileType.CSV]: {
    regular: faFileCsvLight,
    solid: faFileCsvSolid,
  },
  [FileType.DOCUMENT]: {
    regular: faFileWordLight,
    solid: faFileWordSolid,
  },
  [FileType.IMAGE]: {
    regular: faFileImageLight,
    solid: faFileImageSolid,
  },
  [FileType.MARKDOWN]: {
    regular: faFileCodeLight,
    solid: faFileCodeSolid,
  },
  [FileType.OTHER]: {
    regular: faFileLight,
    solid: faFileSolid,
  },
  [FileType.PDF]: {
    regular: faFilePdfLight,
    solid: faFilePdfSolid,
  },
  [FileType.PRESENTATION]: {
    regular: faFilePowerpointLight,
    solid: faFilePowerpointSolid,
  },
  [FileType.SPREADSHEET]: {
    regular: faFileExcelLight,
    solid: faFileExcelSolid,
  },
  [FileType.TEXT]: {
    regular: faFileAltLight,
    solid: faFileAltSolid,
  },
  [FileType.VIDEO]: {
    regular: faFileVideoLight,
    solid: faFileVideoSolid,
  },
  [FileType.XML]: {
    regular: faFileXmlLight,
    solid: faFileXmlSolid,
  },
  [FileType.ZIP]: {
    regular: faFileZipLight,
    solid: faFileZipSolid,
  },
};

const mimeTypeToFileType: Record<string, FileType> = {
  "application/csv": FileType.CSV,
  "application/javascript": FileType.CODE,
  "application/json": FileType.CODE,
  "application/msword": FileType.DOCUMENT,
  "application/pdf": FileType.PDF,
  "application/rtf": FileType.DOCUMENT,
  "application/sql": FileType.CODE,
  "application/vnd.ms-excel": FileType.SPREADSHEET,
  "application/vnd.ms-powerpoint": FileType.PRESENTATION,
  "application/vnd.openxmlformats-officedocument.presentationml.presentation":
    FileType.PRESENTATION,
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": FileType.SPREADSHEET,
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document": FileType.DOCUMENT,
  "application/x-7z-compressed": FileType.ZIP,
  "application/x-httpd-php": FileType.CODE,
  "application/x-rar-compressed": FileType.ZIP,
  "application/x-tar": FileType.ZIP,
  "application/x-yaml": FileType.CODE,
  "application/x-zip-compressed": FileType.ZIP,
  "application/xml": FileType.XML,
  "application/zip": FileType.ZIP,
  "text/css": FileType.CODE,
  "text/csv": FileType.CSV,
  "text/html": FileType.CODE,
  "text/markdown": FileType.MARKDOWN,
  "text/plain": FileType.TEXT,
  "text/x-java-source": FileType.CODE,
  "text/x-python": FileType.CODE,
  "text/xml": FileType.XML,
  "text/yaml": FileType.CODE,
};

const nameToFileType: Record<string, FileType> = {
  "/\\.(doc|docx|rtf|odt)$/i": FileType.DOCUMENT,
  "/\\.(jpe?g|png|gif|bmp|webp|svg)$/i": FileType.IMAGE,
  "/\\.(js|ts|jsx|tsx|java|py|rb|php|c|cpp|cs|html|css|json|xml|sql|sh|yml|yaml)$/i": FileType.CODE,
  "/\\.(mp3|wav|ogg|flac|aac|m4a)$/i": FileType.AUDIO,
  "/\\.(mp4|mov|avi|wmv|flv|mkv|webm)$/i": FileType.VIDEO,
  "/\\.(ppt|pptx|odp)$/i": FileType.PRESENTATION,
  "/\\.(txt|md)$/i": FileType.TEXT,
  "/\\.(xls|xlsx|ods)$/i": FileType.SPREADSHEET,
  "/\\.(zip|rar|7z|tar|gz|bz2)$/i": FileType.ZIP,
  "/\\.csv$/i": FileType.CSV,
  "/\\.pdf$/i": FileType.PDF,
};

export const getFileType = (file: FileIconFile): FileType => {
  let fileType: FileType | undefined;

  const fileMimeType = Object.keys(mimeTypeToFileType).find((mimeType) => {
    if (mimeType.startsWith("/") && mimeType.endsWith("/")) {
      return new RegExp(mimeType.slice(1, -1)).test(file.contentType);
    }
    return mimeType === file.contentType;
  });
  if (fileMimeType) {
    fileType = mimeTypeToFileType[fileMimeType];
  }

  if (!fileType) {
    if (/^audio\//.test(file.contentType)) {
      fileType = FileType.AUDIO;
    } else if (/^image\//.test(file.contentType)) {
      fileType = FileType.IMAGE;
    } else if (/^video\//.test(file.contentType)) {
      fileType = FileType.VIDEO;
    } else if (/^text\//.test(file.contentType)) {
      fileType = FileType.TEXT;
    } else {
      fileType = FileType.OTHER;
    }
  }

  if (!fileType) {
    const fileNameKey = Object.keys(nameToFileType).find((pattern) => {
      if (pattern.startsWith("/") && pattern.endsWith("/")) {
        return new RegExp(pattern.slice(1, -1), "i").test(file.name);
      }
      return file.name === pattern;
    });

    if (fileNameKey) {
      fileType = nameToFileType[fileNameKey];
    }
  }

  return fileType || FileType.OTHER;
};

export interface FileIconFile {
  contentType: string;
  name: string;
}

export interface FileIconProps extends Omit<AvatarProps, "className" | "icon"> {
  className?: string;
  file: FileIconFile;
  iconStyle?: "regular" | "solid";
}

const FileIcon = ({ className, file, iconStyle = "regular", ...props }: FileIconProps) => {
  const fileType = useMemo(() => getFileType(file), [file]);
  return (
    <Avatar
      className={cn(styles.container, styles[fileType], className)}
      icon={<FontAwesomeIcon icon={fileTypeToIcons[fileType][iconStyle]} />}
      {...props}
    />
  );
};

export default FileIcon;
